import { CollectionResponse } from '../../interfaces/api/api';
import api from '../../config/api';
import { getJsonResponse } from '../../utils/ApiUtils';
import {
  AppointmentType,
  CreateAppointmentOptions,
  LegacyAppointment,
  LegacyNote,
  LegacyReminder,
  LegacyTask,
} from '../../interfaces/api/Collaborative';
import { Order } from '../../interfaces/Table';
import useAuth from './useAuth';

const useCollaborative = () => {
  const { fetch } = useAuth();

  const getAppointmentTypes = async (): Promise<CollectionResponse<AppointmentType>> => {
    const { endpoint, ...options } = api.collaborative.getAppointmentTypes();

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<AppointmentType>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const addAppointment = async (addOptions: CreateAppointmentOptions): Promise<LegacyAppointment> => {
    const { endpoint, ...options } = api.collaborative.addAppointment(addOptions);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<LegacyAppointment>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getAppointments = async (
    ids: number[],
    page: number,
    limit: number,
    orderBy: keyof LegacyAppointment,
    order: Order
  ): Promise<CollectionResponse<LegacyAppointment>> => {
    if (ids.length <= 0) {
      return { items: [], total: 0, limit: 0, page: 0 };
    }

    const { endpoint, ...options } = api.collaborative.getAppointments(ids, page, limit, orderBy, order);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<LegacyAppointment>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getAppointment = async (id: number): Promise<LegacyAppointment> => {
    const { endpoint, ...options } = api.collaborative.getAppointment(id);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<LegacyAppointment>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getNotes = async (
    ids: number[],
    page: number,
    limit: number,
    orderBy: keyof LegacyNote,
    order: Order
  ): Promise<CollectionResponse<LegacyNote>> => {
    if (ids.length <= 0) {
      return { items: [], total: 0, limit: 0, page: 0 };
    }

    const { endpoint, ...options } = api.collaborative.getNotes(ids, page, limit, orderBy, order);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<LegacyNote>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getNote = async (id: number): Promise<LegacyNote> => {
    const { endpoint, ...options } = api.collaborative.getNote(id);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<LegacyNote>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getTasks = async (
    ids: number[],
    page: number,
    limit: number,
    orderBy: keyof LegacyTask,
    order: Order
  ): Promise<CollectionResponse<LegacyTask>> => {
    if (ids.length <= 0) {
      return { items: [], total: 0, limit: 0, page: 0 };
    }

    const { endpoint, ...options } = api.collaborative.getTasks(ids, page, limit, orderBy, order);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<LegacyTask>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getTask = async (id: number): Promise<LegacyTask> => {
    const { endpoint, ...options } = api.collaborative.getTask(id);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<LegacyTask>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getReminders = async (
    ids: number[],
    page: number,
    limit: number,
    orderBy: keyof LegacyReminder,
    order: Order
  ): Promise<CollectionResponse<LegacyReminder>> => {
    if (ids.length <= 0) {
      return { items: [], total: 0, limit: 0, page: 0 };
    }

    const { endpoint, ...options } = api.collaborative.getReminders(ids, page, limit, orderBy, order);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<LegacyReminder>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getReminder = async (id: number): Promise<LegacyReminder> => {
    const { endpoint, ...options } = api.collaborative.getReminder(id);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<LegacyReminder>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  return {
    getAppointmentTypes,
    addAppointment,
    getAppointments,
    getAppointment,
    getNotes,
    getNote,
    getTasks,
    getTask,
    getReminders,
    getReminder,
  };
};

export default useCollaborative;
